import { computeMpmAssignedMpOccupancy } from "./mpmCapacity";
import {
  createDefaultMpmState,
  normalizeMpmState,
  type MpmPresetSnapshot,
  type MpmState,
} from "./mpmState";

export function captureMpmPresetSnapshot(state: MpmState): MpmPresetSnapshot {
  const normalized = normalizeMpmState(state);
  return {
    hovTargets: captureAssignmentSection(normalized.hovTargets),
    hovGainUpgrades: captureAssignmentSection(normalized.hovGainUpgrades),
    amplifierPanels: captureAssignmentSection(normalized.amplifierPanels),
    amplifierUpgrades: captureAssignmentSection(normalized.amplifierUpgrades),
    mhovExecution: {
      enabled: normalized.mhovExecution.enabled,
      assigned: normalized.mhovExecution.assigned,
    },
    nhovExecution: captureAssignmentSection(normalized.nhovExecution),
    ohovExecution: {
      enabled: normalized.ohovExecution.enabled,
      assigned: normalized.ohovExecution.assigned,
    },
    exploration: {
      enabled: normalized.exploration.enabled,
      assignedSlotIds: [...normalized.exploration.assignedSlotIds],
    },
    numericSettings: {
      mhovExecution: {
        multiplier: normalized.mhovExecution.multiplier,
        minimumResetIntervalMs: normalized.mhovExecution.minimumResetIntervalMs,
      },
      nhovExecution: {
        conditions: Object.fromEntries(
          Object.entries(normalized.nhovExecution.conditions).map(
            ([branchId, condition]) => [branchId, { ...condition }],
          ),
        ) as typeof normalized.nhovExecution.conditions,
      },
      ohovExecution: {
        minimumHovCore: normalized.ohovExecution.minimumHovCore,
        minimumIntervalMs: normalized.ohovExecution.minimumIntervalMs,
      },
    },
  };
}

export function withSavedMpmPreset(
  state: MpmState,
  presetIndex: number,
): MpmState {
  const normalized = normalizeMpmState(state);
  if (!Number.isInteger(presetIndex) || presetIndex < 0) return normalized;
  if (presetIndex >= normalized.presets.length) return normalized;
  return normalizeMpmState({
    ...normalized,
    presets: normalized.presets.map((preset, index) =>
      index === presetIndex ? captureMpmPresetSnapshot(normalized) : preset,
    ),
  });
}

export function computeMpmPresetAssignedMp(preset: MpmPresetSnapshot): number {
  const state = createDefaultMpmState();
  return computeMpmAssignedMpOccupancy({
    ...state,
    hovTargets: { ...state.hovTargets, ...preset.hovTargets },
    hovGainUpgrades: {
      ...state.hovGainUpgrades,
      ...preset.hovGainUpgrades,
    },
    amplifierPanels: {
      ...state.amplifierPanels,
      ...preset.amplifierPanels,
    },
    amplifierUpgrades: {
      ...state.amplifierUpgrades,
      ...preset.amplifierUpgrades,
    },
    mhovExecution: { ...state.mhovExecution, ...preset.mhovExecution },
    nhovExecution: { ...state.nhovExecution, ...preset.nhovExecution },
    ohovExecution: { ...state.ohovExecution, ...preset.ohovExecution },
    exploration: { ...state.exploration, ...preset.exploration },
  });
}

function captureAssignmentSection<T>(section: {
  readonly enabled: boolean;
  readonly assignments: readonly T[];
}): { readonly enabled: boolean; readonly assignments: readonly T[] } {
  return {
    enabled: section.enabled,
    assignments: [...section.assignments],
  };
}
